import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  DataGridPro,
  GridColumns,
  GridRowParams,
  GridSelectionModel,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  LicenseInfo,
} from '@mui/x-data-grid-pro'
import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { toast } from 'react-toastify'
import { PropsFromReduxTasks } from '../containers/TasksContainer'
import { ITask } from '../reducers'
import TaskDetailPanel from './TaskDetailPanel'

LicenseInfo.setLicenseKey(
  'x0jTPl0USVkVZV0SsMjM1kDNyADM5cjM2ETPZJVSQhVRsIDN0YTM6IVREJ1T0b9586ef25c9853decfa7709eee27a1e',
)

/** Les trois onglets, et le filtre qu'ils posent sur la requête. */
type Onglet = 'A_TRAITER' | 'CAUSE_LEVEE' | 'TRAITEES'

const FILTRE_ONGLET: Record<
  Onglet,
  { isOpen?: boolean; causeStillHolds?: boolean }
> = {
  A_TRAITER: { isOpen: true },
  // La revalidation n'est pas une colonne : l'API la calcule et sait filtrer
  // dessus. Voir UserTaskService.identifiantsParVerdict.
  CAUSE_LEVEE: { isOpen: true, causeStillHolds: false },
  TRAITEES: { isOpen: false },
}

const TAILLE_PAGE = 25

const TasksComponent = (props: PropsFromReduxTasks) => {
  const [onglet, setOnglet] = useState<Onglet>('A_TRAITER')
  const [page, setPage] = useState(0)
  const [recherche, setRecherche] = useState('')
  const [typeChoisi, setTypeChoisi] = useState('')
  const [selection, setSelection] = useState<GridSelectionModel>([])
  const [confirmation, setConfirmation] = useState(false)
  const [clotureEnCours, setClotureEnCours] = useState(false)
  const [centreChoisi, setCentreChoisi] = useState<number | ''>('')

  // Le filtre par centre n'a de sens que pour qui voit plusieurs centres.
  const ROLES_EXPLOITATION = [
    'SuperAdmin',
    'PromAdmin',
    'PromDoctor',
    'PromNurse',
    'PromSales',
  ]
  const voitTousLesCentres = (props.roles ?? []).some((role: string) =>
    ROLES_EXPLOITATION.includes(role),
  )

  const charger = useCallback(() => {
    props
      .getTasks(TAILLE_PAGE, page, {
        ...FILTRE_ONGLET[onglet],
        name: typeChoisi || undefined,
        search: recherche || undefined,
        centerId: centreChoisi === '' ? undefined : Number(centreChoisi),
      })
      .catch(() => undefined)
    props.getTaskSummary().catch(() => undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onglet, page, recherche, typeChoisi, centreChoisi])

  useEffect(() => {
    charger()
  }, [charger])

  useEffect(() => {
    if (voitTousLesCentres) {
      props.getCenters().catch(() => undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voitTousLesCentres])

  // Les types viennent du résumé, jamais d'une liste codée en dur : data-nest
  // en émet dix-huit aujourd'hui, trois de plus qu'il y a une semaine, et la
  // liste bougera encore.
  const typesDisponibles = useMemo(
    () => props.summary.byName.map((ligne) => ligne.name),
    [props.summary.byName],
  )

  const columns: GridColumns = [
    {
      field: 'createdAt',
      headerName: 'Ouverte le',
      width: 130,
      renderCell: ({ row }) => (
        <Tooltip title={`il y a ${row.ageInDays} jours`}>
          <span>{new Date(row.createdAt).toLocaleDateString('fr-FR')}</span>
        </Tooltip>
      ),
    },
    {
      field: 'ageInDays',
      headerName: 'Âge',
      width: 90,
      renderCell: ({ row }) => (
        <Typography
          variant="body2"
          sx={{ color: row.ageInDays > 90 ? 'error.main' : 'text.primary' }}
        >
          {row.ageInDays} j
        </Typography>
      ),
    },
    { field: 'name', headerName: 'Type', width: 240 },
    {
      field: 'priority',
      headerName: 'Priorité',
      width: 110,
      renderCell: ({ row }) =>
        row.priority === 'URGENT' ? (
          <Chip label="Urgent" color="error" size="small" />
        ) : (
          <Chip label="Normal" size="small" variant="outlined" />
        ),
    },
    {
      field: 'reference',
      headerName: 'Référence',
      width: 200,
      renderCell: ({ row }) =>
        row.resolvedReference.link ? (
          <Link href={row.resolvedReference.link}>
            {row.resolvedReference.label}
          </Link>
        ) : (
          <span>{row.resolvedReference.label}</span>
        ),
    },
    {
      field: 'causeStillHolds',
      headerName: 'Cause',
      width: 140,
      renderCell: ({ row }) => {
        if (row.causeStillHolds === false) {
          return <Chip label="Levée" color="success" size="small" />
        }
        if (row.causeStillHolds === true) {
          return <Chip label="Tient" size="small" variant="outlined" />
        }
        // `null` n'est pas « levée » : aucun prédicat ne couvre ce type.
        return (
          <Tooltip title="Aucune vérification automatique pour ce type de tâche">
            <span>—</span>
          </Tooltip>
        )
      },
    },
    { field: 'companyName', headerName: 'Centre', width: 200 },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      minWidth: 200,
      renderCell: ({ row }) => (
        // Tronquée ici, entière dans le panneau : `addTask` concatène les
        // occurrences et une description peut faire des dizaines de lignes.
        <Tooltip title="Déplier la ligne pour lire l’intégralité">
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {row.description.split('\n')[0]}
          </span>
        </Tooltip>
      ),
    },
  ]

  // Après un traitement, la liste ET les compteurs sont rechargés : la tâche
  // quitte l'onglet « à traiter », et le badge doit suivre. Sans ce
  // rafraîchissement, la ligne resterait affichée comme ouverte.
  const traiter = useCallback(
    (id: string, status: 'DONE' | 'REJECTED', comment: string | undefined) =>
      props.transitionTask(id, status, comment).then((res) => {
        charger()
        return res
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [charger],
  )

  const commenter = useCallback(
    (id: string, comment: string) =>
      props.commentTask(id, comment).then((res) => {
        charger()
        return res
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [charger],
  )

  const getDetailPanelContent = useCallback(
    ({ row }: GridRowParams<ITask>) => (
      <TaskDetailPanel row={row} onTransition={traiter} onComment={commenter} />
    ),
    [traiter, commenter],
  )
  const getDetailPanelHeight = useCallback(() => 340, [])

  // La clôture groupée n'a de sens que là où toutes les lignes partagent le
  // même motif. Sur les autres onglets, chaque tâche appelle sa propre décision.
  const clotureGroupeePossible = onglet === 'CAUSE_LEVEE'

  // Quelles raisons de levée dans la sélection ? La formulation du dialogue
  // dépend du mélange : annoncer « le problème n'existe plus » sur une
  // référence rejetée serait faux.
  const tachesSelectionnees = props.tasks.filter((t) =>
    selection.includes(t.id),
  )
  const horsPerimetre = tachesSelectionnees.some(
    (t) => t.causeLiftedReason === 'OUT_OF_SCOPE',
  )
  const resolues = tachesSelectionnees.some(
    (t) =>
      t.causeStillHolds === false && t.causeLiftedReason !== 'OUT_OF_SCOPE',
  )

  const cloturerLaSelection = () => {
    setClotureEnCours(true)
    props
      .bulkCloseTasks(selection.map(String))
      .then((res: any) => {
        const fermees = res?.closed ?? 0
        const ecartees = res?.skipped?.length ?? 0
        // L'écart est dit, jamais tu : le serveur recalcule le verdict au
        // moment d'écrire, et une tâche redevenue vivante entre l'affichage et
        // le clic n'est pas fermée. Annoncer « 12 fermées » quand il y en a 9
        // serait pire que ne rien annoncer.
        if (ecartees) {
          toast.warning(
            `${fermees} tâche(s) fermée(s), ${ecartees} écartée(s) : leur cause tient encore.`,
          )
        } else {
          toast.success(`${fermees} tâche(s) fermée(s).`)
        }
        setSelection([])
        setConfirmation(false)
        charger()
      })
      .catch(() => toast.error('La clôture groupée a échoué.'))
      .finally(() => setClotureEnCours(false))
  }

  return (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Tâches
      </Typography>

      <Tabs
        value={onglet}
        onChange={(_, valeur: Onglet) => {
          setOnglet(valeur)
          setPage(0)
        }}
        sx={{ mb: 1 }}
      >
        <Tab
          value="A_TRAITER"
          label={
            <Badge badgeContent={props.summary.open} color="error" max={999}>
              <span style={{ paddingRight: 14 }}>À TRAITER</span>
            </Badge>
          }
        />
        <Tab
          value="CAUSE_LEVEE"
          label={
            <Badge
              badgeContent={props.summary.causeLifted}
              color="success"
              max={999}
            >
              <span style={{ paddingRight: 14 }}>CAUSE LEVÉE</span>
            </Badge>
          }
        />
        <Tab value="TRAITEES" label="TRAITÉES" />
      </Tabs>

      <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
        <TextField
          size="small"
          label="Rechercher"
          value={recherche}
          onChange={(e) => {
            setRecherche(e.target.value)
            setPage(0)
          }}
        />
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel>Type de tâche</InputLabel>
          <Select
            label="Type de tâche"
            value={typeChoisi}
            onChange={(e) => {
              setTypeChoisi(e.target.value)
              setPage(0)
            }}
          >
            <MenuItem value="">Tous</MenuItem>
            {typesDisponibles.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {voitTousLesCentres && (
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel>Centre</InputLabel>
            <Select
              label="Centre"
              value={centreChoisi}
              onChange={(e) => {
                setCentreChoisi(e.target.value as number | '')
                setPage(0)
              }}
            >
              <MenuItem value="">Tous les centres</MenuItem>
              {(props.centers ?? []).map((centre: any) => (
                <MenuItem key={centre.id} value={centre.id}>
                  {centre.companyName ?? centre.company_name ?? centre.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {clotureGroupeePossible && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Button
            variant="contained"
            color="success"
            disabled={!selection.length}
            onClick={() => setConfirmation(true)}
          >
            Fermer la sélection ({selection.length})
          </Button>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {props.summary.causeLifted} tâche(s) au total dont la cause a
            disparu.
          </Typography>
        </Box>
      )}

      <DataGridPro
        autoHeight
        checkboxSelection={clotureGroupeePossible}
        selectionModel={selection}
        onSelectionModelChange={setSelection}
        loading={props.loading}
        rows={props.tasks}
        columns={[
          {
            ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
            headerName: '',
          },
          ...columns,
        ]}
        getRowId={(row) => row.id}
        // Pagination serveur : le tri par défaut est appliqué côté API — les
        // plus anciennes d'abord, pour qu'une tâche de deux ans soit en haut
        // de l'écran et non en page 4.
        pagination
        paginationMode="server"
        rowCount={props.totalPages * TAILLE_PAGE}
        page={page}
        pageSize={TAILLE_PAGE}
        rowsPerPageOptions={[TAILLE_PAGE]}
        onPageChange={setPage}
        disableSelectionOnClick
        disableColumnMenu
        getDetailPanelContent={getDetailPanelContent}
        getDetailPanelHeight={getDetailPanelHeight}
        localeText={{
          noRowsLabel:
            onglet === 'CAUSE_LEVEE'
              ? 'Aucune tâche dont la cause ait disparu.'
              : 'Aucune tâche.',
        }}
        sx={{ border: 'none', background: '#fff' }}
      />

      <Dialog open={confirmation} onClose={() => setConfirmation(false)}>
        <DialogTitle>Fermer {selection.length} tâche(s)</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Ces tâches seront marquées comme traitées. Le motif dépend de chaque
            tâche : si le problème a disparu des données, ou si la référence a été
            déclarée hors périmètre de facturation.
          </DialogContentText>
          <Alert severity="info" sx={{ mt: 2 }}>
            Le contrôle est refait au moment de la clôture : une tâche dont le
            problème serait réapparu depuis l’affichage ne sera pas fermée, et
            vous sera signalée.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmation(false)} disabled={clotureEnCours}>
            Annuler
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={cloturerLaSelection}
            disabled={clotureEnCours}
          >
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default TasksComponent
